import DocumentPagination from "../Common/DocumentPagination";
import "../Reception/ReceptionCompact.css";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Spinner,
} from "reactstrap";
import { FaCheckCircle, FaEdit, FaFilter, FaPlus, FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import ReceiptNoteNew from "./ReceiptNoteNew";
import { formatPrice } from "../../helpers/utility";
import { useNavigate } from "react-router-dom";
import { CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW } from "../../routes/routeConstants";
import "./pos-reception.css";
import POSHeader from "./POSHeader";

const CReceptionPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchInputRef = useRef(null);

    const { receiptNotes, isLoading, totalPages, totalElements, pageNumber, pageSize } = useSelector((state) => state.receiptNote);
    const { role, businessName, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
    const isAdmin = role === "ROLE_ADMIN";
    const [dateModal, setDateModal] = useState(false);

    const [activeTab, setActiveTab] = useState("RECEPTION");
    const [showModal, setShowModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);

    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    const formatDate = (d) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const [filters, setFilters] = useState({
        dateFrom: formatDate(now),
        dateTo: formatDate(now),
        query: "",
        page: 0,
        size: 10,
    });

    useEffect(() => {
        dispatch(receiptNoteActions.search(filters));
    }, [dispatch, showModal, filters.dateFrom, filters.dateTo, filters.page]);

    const debouncedSearch = () => {
        setFilters(current => ({ ...current, page: 0 }));
        dispatch(receiptNoteActions.search({ ...filters, page: 0 }));
    };

    const clearFilters = () => {
        const clearedFilters = {
            dateFrom: formatDate(now),
            dateTo: formatDate(now),
            query: "",
        page: 0,
        size: 10,
        };
        setFilters(clearedFilters);
        dispatch(receiptNoteActions.search(clearedFilters));
    };

    const handleValidate = (note) => {
        Swal.fire({
            title: t("common.confirm"),
            text: t("reception.confirmValidateMessage"),
            icon: "warning",
            showCancelButton: true,
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(
                    receiptNoteActions.validateReceiptNote({
                        id: note.id,
                        onSuccess: () => {
                            Swal.fire(t("common.saved"), "", "success");
                            dispatch(receiptNoteActions.search(filters));
                        },
                    })
                );
            }
        });
    };

    const getStatusBadge = (status) => {
        if (status === "VALIDE") return <Badge color="success">Confirmée</Badge>;
        return <Badge color="secondary">À confirmer</Badge>;
    };

    return (
        <Container fluid className="pos-page reception-compact reception-list">
            <POSHeader
                businessName={businessName}
                fullName={fullName}
                commercialId={commercialId}
                pointOfSaleId={pointOfSaleId}
                isAdmin={isAdmin}
                activeTab={activeTab}
                onChangeTab={(tab) => {
                    setActiveTab(tab);
                    if (tab === "VENTES") navigate(POS_PAGE);
                    if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
                    if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
                    if (tab === "AVANCE") navigate(PAY_PAGE);
                    if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
                    if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
                }}
            />

            <Row>
                <Col md={12}>
                    <Card className="pos-card">
                        <CardBody className="pos-card-body">
                            {isLoading ? (
                                <div className="pos-loading">
                                    <Spinner color="primary" />
                                    <div className="mt-2 text-muted">{t("common.loading")}</div>
                                </div>
                            ) : (
                                <>
                                    {/* ===== POS TOOLBAR ===== */}
                                    <div className="pos-toolbar">
                                        <div className="pos-toolbar-title">
                                            <span className="pos-title-badge">RÉCEPTIONS</span>
                                            <small className="text-muted">
                                                {receiptNotes?.length || 0} docs
                                            </small>
                                        </div>

                                        <div className="pos-toolbar-controls">
                                            <div className="pos-search-bar">
                                                <FaSearch className="pos-search-icon" />
                                                <Input
                                                    innerRef={searchInputRef}
                                                    type="text"
                                                    value={filters.query}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, query: e.target.value })
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") debouncedSearch();
                                                    }}
                                                    placeholder="Numéro du bon ou fournisseur"
                                                    className="pos-search-input"
                                                />
                                            </div>

                                            <Button
                                                color="light"
                                                className="pos-filter-btn"
                                                onClick={() => setDateModal(true)}
                                            >
                                                {filters.dateFrom} → {filters.dateTo}
                                            </Button>
                                            <Button
                                                color="secondary"
                                                className="pos-icon-btn"
                                                onClick={clearFilters}
                                            >
                                                <FaFilter /> Aujourd’hui
                                            </Button>

                                            <Button
                                                color="primary"
                                                className="pos-icon-btn"
                                                onClick={() => { setSelectedNote(null); setShowModal(true); }}
                                            >
                                                <FaPlus /> Nouvelle réception
                                            </Button>
                                        </div>
                                    </div>

                                    {/* ===== TABLE ===== */}
                                    <div className="pos-table-wrap">
                                        <table className="table pos-table align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Numéro</th>
                                                    <th>Date</th>
                                                    <th>Fournisseur</th>
                                                    <th>Total</th>
                                                    <th>Statut</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {receiptNotes.map((note) => (
                                                    <tr key={note.id} className="pos-row">
                                                        <td>{note.numero}</td>
                                                        <td>{note.dateReception}</td>
                                                        <td>{note.fournisseurNom}</td>
                                                        <td>{formatPrice(note.montantTotalTTC)}</td>
                                                        <td>{getStatusBadge(note.statut)}</td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                color="warning"
                                                                className="pos-icon-btn me-1"
                                                                onClick={() => {
                                                                    setSelectedNote(note);
                                                                    setShowModal(true);
                                                                }}
                                                            >
                                                                <FaEdit /> Voir
                                                            </Button>

                                                            {note.statut === "EN_ATTENTE" && (
                                                                <Button
                                                                    size="sm"
                                                                    color="success"
                                                                    className="pos-icon-btn"
                                                                    onClick={() => handleValidate(note)}
                                                                >
                                                                    <FaCheckCircle /> Confirmer
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <DocumentPagination pageNumber={pageNumber} totalPages={totalPages} totalElements={totalElements} pageSize={pageSize} onPageChange={page => setFilters(current => ({ ...current, page }))} />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="xl" className="reception-compact-modal">
                <ModalHeader toggle={() => setShowModal(false)}>
                    Réception {selectedNote ? selectedNote.numero : ""}
                </ModalHeader>
                <ModalBody>
                    <ReceiptNoteNew
                        onClose={() => setShowModal(false)}
                        reception={selectedNote}
                    />
                </ModalBody>
            </Modal>

            <Modal isOpen={dateModal} toggle={() => setDateModal(false)} centered className="reception-compact-modal">
                <ModalHeader toggle={() => setDateModal(false)}>
                    Filtrer par date
                </ModalHeader>

                <ModalBody>
                    <Row className="g-3">
                        <Col md={6}>
                            <label className="fw-bold">Date début</label>
                            <Input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) =>
                                    setFilters({ ...filters, dateFrom: e.target.value })
                                }
                            />
                        </Col>

                        <Col md={6}>
                            <label className="fw-bold">Date fin</label>
                            <Input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) =>
                                    setFilters({ ...filters, dateTo: e.target.value })
                                }
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <div className="p-3 d-flex justify-content-end gap-2">
                    <Button
                        color="secondary"
                        onClick={() => setDateModal(false)}
                    >
                        Annuler
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => {
                            dispatch(receiptNoteActions.search(filters));
                            setDateModal(false);
                        }}
                    >
                        Appliquer
                    </Button>
                </div>
            </Modal>

        </Container>
    );
};

export default CReceptionPage;
